/* eslint-disable prefer-spread */
/* eslint-disable max-lines */
import { Button, Col, Modal, Row } from 'reactstrap';
import { useEffect, useState } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { toLocaleFormatSlash } from '../../../../helpers/dateFormat';
import { getW4FormConfig } from '../../../../helpers/w4FormConfig';
import { safeSetTextField } from '../../../../helpers/pdfFieldHelpers';
import { drawSignatureImage } from '../../../../helpers/embedSignatureImage';

type W4DocumentViewModalProps = {
  isOpen: boolean;
  data: any;
  toggleModal: (e?: any) => void;
};

const W4DocumentViewModal = ({ isOpen, data, toggleModal }: W4DocumentViewModalProps) => {
  const [pdf, setPdf] = useState('');

  const readOnlyFields = (pdfForm: any) => {
    try {
      const fieldNames = pdfForm.getFields();
      for (const f of fieldNames) {
        const type = f.constructor.name;
        const fieldName = f.getName();
        if (type === 'PDFTextField') {
          pdfForm.getTextField(fieldName).enableReadOnly();
        }
        if (type === 'PDFDropdown') {
          pdfForm.getDropdown(fieldName).enableReadOnly();
        }
        if (type === 'PDFCheckBox') {
          pdfForm.getCheckBox(fieldName).enableReadOnly();
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (isOpen) createPdf();
  }, [isOpen, data]);

  const createPdf = async () => {
    try {
      if (!data) return;

      const formYear = data?.options?.formYear ?? 2023;
      const versionConfig = getW4FormConfig(formYear);
      const templateUrl = data?.options?.pdfUrl || data?.pdfUrl || versionConfig.pdfUrl;
      const url = data?.uploadedPdf || templateUrl || '';
      const coordinatesSign = data?.options?.coordinatesSign ?? versionConfig.coordinatesSign;
      const coordinatesDate = {
        ...versionConfig.coordinatesDate,
        ...(data?.options?.coordinatesDate || {}),
        color: data?.options?.coordinatesDate?.color ?? rgb(0, 0, 0),
      };

      const arrayBuffer = await fetch(url).then((res) => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pdfForm = pdfDoc.getForm();
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);

      if (firstPage) {
        if (data?.options?.signPng) {
          const pngImage = await pdfDoc.embedPng(data?.options?.signPng);
          drawSignatureImage(firstPage, pngImage, coordinatesSign);
        }
        if (data?.options?.signatureDate) {
          firstPage.drawText(toLocaleFormatSlash(data?.options?.signatureDate), {
            x: coordinatesDate.x,
            y: coordinatesDate.y,
            size: coordinatesDate.size ?? 12,
            font: timesRomanFont,
            color: coordinatesDate.color ?? rgb(0, 0, 0),
          });
        }
      }

      if (versionConfig.useFormFields) {
        readOnlyFields(pdfForm);
        const fieldNames = pdfForm.getFields().map((f) => f.getName());
        if (fieldNames.length) {
          const idx = versionConfig.fieldIndices;
          const emp = idx?.employerName ?? 15;
          const dat = idx?.date ?? 16;
          const ei = idx?.ein ?? 17;
          const getAmountIdx = idx?.getAmountFormIndex ?? ((i: number) => i);
          const setTextField = (index: number, value: string | undefined) => {
            if (!fieldNames[index]) return;
            safeSetTextField(pdfForm, fieldNames[index], value);
          };
          const setCheckBox = (index: number, shouldCheck: boolean) => {
            if (fieldNames[index]) {
              try {
                const field = pdfForm.getCheckBox(fieldNames[index]);
                field.enableReadOnly();
                if (shouldCheck) field.check();
              } catch {
                void 0;
              }
            }
          };
          const fields = data?.options?.fields;
          setTextField(0, fields?.firstName);
          setTextField(1, fields?.lastName);
          setTextField(2, fields?.address);
          setTextField(3, fields?.city);
          setTextField(4, fields?.ssn);
          setTextField(emp, fields?.employerName);
          setTextField(dat, fields?.date ? toLocaleFormatSlash(fields.date) : undefined);
          setTextField(ei, fields?.ein);
          setCheckBox(5, fields?.married === '1');
          setCheckBox(6, fields?.married === '2');
          setCheckBox(7, fields?.married === '3');
          if (idx?.step3Total != null) {
            const amounts = data?.options?.fields?.amounts || [];
            const a9 = amounts.find((a: { i?: number; v?: number }) => a.i === 9)?.v ?? 0;
            const a10 = amounts.find((a: { i?: number; v?: number }) => a.i === 10)?.v ?? 0;
            const step3Total = Number(a9) + Number(a10);
            setTextField(idx.step3Total, String(step3Total));
          }
          for (const n of data?.options?.fields?.amounts || []) {
            if (n?.i === undefined) continue;
            setTextField(getAmountIdx(n.i), String(n?.v ?? 0));
          }
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      const docUrl = URL.createObjectURL(blob);
      setPdf(docUrl);
    } catch (e) {
      console.log(e);
    }
  };

  const handleDownloadPdf = () => {
    if (!pdf) {
      return;
    }
    const link = document.createElement('a');
    link.href = pdf;
    const filename = `w4.pdf`;
    link.setAttribute('download', filename);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Modal className="modal-dialog-centered" size="xl" isOpen={isOpen} toggle={(e: any) => toggleModal(e)}>
        <div className="modal-header pb-1">
          <h5 className="modal-title">W4</h5>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={(e) => toggleModal(e)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body pt-0">
          <Row className="mt-2">
            <Col md="12" lg="12">
              <div className="d-flex justify-content-end">
                <Button color="primary" onClick={handleDownloadPdf} size="sm">
                  Download (PDF)
                </Button>
              </div>
            </Col>
            <Col lg="12" className="mt-4" style={{ maxHeight: '500px', overflow: 'scroll' }}>
              <iframe src={pdf} style={{ minHeight: 500, width: '100%' }} />
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

export default W4DocumentViewModal;
